export type ForumThread = {
  id: string;
  title: string;
  preview: string;
  authorName: string;
  authorInitials: string;
  category: string;
  subcategory?: string;
  city: string;
  upvotes: number;
  replyCount: number;
  createdAt: number;
  timeAgo: string;
  pinned: boolean;
};

export const CATEGORIES = [
  { key: 'all', label: 'All Topics', emoji: '🌐' },
  { key: 'immigration', label: 'Immigration & Visa', emoji: '🛂' },
  { key: 'jobs', label: 'Jobs & Referrals', emoji: '💼' },
  { key: 'housing-forum', label: 'Housing', emoji: '🏠' },
  { key: 'life', label: 'Life in USA', emoji: '🗽' },
  { key: 'buysell', label: 'Buy/Sell', emoji: '🛍️' },
];

export const SUBCATEGORIES: Record<string, { key: string; label: string }[]> = {
  immigration: [
    { key: 'h1b', label: 'H1B Visa' },
    { key: 'green-card', label: 'Green Card / GC' },
    { key: 'f1', label: 'F1 Student Visa' },
    { key: 'opt', label: 'OPT / STEM OPT' },
    { key: 'l1', label: 'L1 Visa' },
    { key: 'eb2-eb3', label: 'EB2 / EB3' },
    { key: 'citizenship', label: 'Citizenship / N-400' },
    { key: 'travel-docs', label: 'Travel & Documents' },
    { key: 'immigration-other', label: 'Other Immigration' },
  ],
  jobs: [
    { key: 'software-engineer', label: 'Software Engineer' },
    { key: 'frontend', label: 'Frontend / UI Dev' },
    { key: 'backend', label: 'Backend Dev' },
    { key: 'fullstack', label: 'Full Stack' },
    { key: 'data-engineer', label: 'Data Engineering' },
    { key: 'data-science', label: 'Data Science / ML' },
    { key: 'devops', label: 'DevOps / Cloud' },
    { key: 'qa', label: 'QA / Testing' },
    { key: 'product', label: 'Product Management' },
    { key: 'it-support', label: 'IT Support / Admin' },
    { key: 'cybersecurity', label: 'Cybersecurity' },
    { key: 'jobs-other', label: 'Other IT Jobs' },
  ],
  'housing-forum': [
    { key: 'shared-room', label: 'Shared Room' },
    { key: 'house-rent', label: 'House for Rent' },
    { key: 'apartment-rent', label: 'Apartment for Rent' },
    { key: 'transfer-lease', label: 'Transfer Lease' },
    { key: 'roommate', label: 'Roommate Wanted' },
    { key: 'short-term', label: 'Short-term / Airbnb' },
    { key: 'housing-tips', label: 'Housing Tips & Advice' },
    { key: 'housing-other', label: 'Other Housing' },
  ],
};

export const POSTING_RULES = [
  {
    icon: '✅',
    title: 'Be Respectful',
    desc: 'Treat all members with respect. No hate speech, personal attacks, or discriminatory language.',
  },
  {
    icon: '📌',
    title: 'Post in the Right Category',
    desc: 'Use the correct category and subcategory so others can find your post easily.',
  },
  {
    icon: '🔍',
    title: 'Search Before Posting',
    desc: 'Check if your question has already been answered to avoid duplicate threads.',
  },
  {
    icon: '🚫',
    title: 'No Spam or Self-Promotion',
    desc: 'Do not post ads, referral links, or promotional content without moderator approval.',
  },
  {
    icon: '🔒',
    title: 'Protect Privacy',
    desc: 'Never share personal details like SSN, passport numbers, or full addresses publicly.',
  },
  {
    icon: '⚖️',
    title: 'No Legal / Financial Advice',
    desc: 'Discussions are for sharing experiences only. Always consult a licensed attorney or advisor.',
  },
  {
    icon: '🇺🇸',
    title: 'Immigration Info Accuracy',
    desc: 'When sharing immigration info, mention your source. Outdated info can mislead others.',
  },
  {
    icon: '🤝',
    title: 'Help Each Other',
    desc: 'This is a community of immigrants helping immigrants. Be generous with your knowledge.',
  },
];

export const CITIES = [
  // California
  'San Jose, CA', 'Fremont, CA', 'Sunnyvale, CA', 'Santa Clara, CA',
  'San Francisco, CA', 'Mountain View, CA', 'Cupertino, CA', 'Milpitas, CA',
  'San Diego, CA', 'Los Angeles, CA', 'Irvine, CA', 'Pleasanton, CA',
  'Belmont, CA', 'Foster City, CA', 'Redwood City, CA', 'Palo Alto, CA',
  'San Ramon, CA', 'Dublin, CA', 'Newark, CA', 'Livermore, CA',
  // Washington
  'Seattle, WA', 'Redmond, WA', 'Bellevue, WA', 'Kirkland, WA',
  'Bothell, WA', 'Sammamish, WA', 'Issaquah, WA',
  // Texas
  'Austin, TX', 'Dallas-Fort Worth, TX', 'Dallas, TX', 'Houston, TX', 'Plano, TX',
  'Irving, TX', 'Frisco, TX', 'Richardson, TX', 'Allen, TX',
  'Sugar Land, TX', 'Katy, TX', 'San Antonio, TX',
  // New York / New Jersey
  'New York, NY', 'Jersey City, NJ', 'Edison, NJ', 'Iselin, NJ',
  'Parsippany, NJ', 'Princeton, NJ', 'Piscataway, NJ', 'Bridgewater, NJ',
  // Illinois
  'Chicago, IL', 'Naperville, IL', 'Schaumburg, IL', 'Lisle, IL',
  // Georgia
  'Atlanta, GA', 'Alpharetta, GA', 'Duluth, GA', 'Suwanee, GA',
  'Johns Creek, GA', 'Cumming, GA',
  // Massachusetts
  'Boston, MA', 'Cambridge, MA', 'Waltham, MA', 'Woburn, MA',
  // North Carolina
  'Raleigh, NC', 'Durham, NC', 'Cary, NC', 'Charlotte, NC', 'Morrisville, NC',
  // Virginia / DC Metro
  'Herndon, VA', 'Chantilly, VA', 'Ashburn, VA', 'McLean, VA',
  'Reston, VA', 'Sterling, VA', 'Centreville, VA', 'Washington, DC',
  // Michigan
  'Detroit, MI', 'Troy, MI', 'Novi, MI', 'Ann Arbor, MI', 'Dearborn, MI',
  // Ohio
  'Columbus, OH', 'Cleveland, OH', 'Cincinnati, OH', 'Dublin, OH',
  // Pennsylvania
  'Philadelphia, PA', 'Pittsburgh, PA', 'Malvern, PA', 'King of Prussia, PA',
  // Minnesota
  'Minneapolis, MN', 'Eden Prairie, MN', 'Minnetonka, MN',
  // Arizona
  'Phoenix, AZ', 'Scottsdale, AZ', 'Tempe, AZ', 'Chandler, AZ', 'Gilbert, AZ',
  // Colorado
  'Denver, CO', 'Boulder, CO', 'Aurora, CO',
  // Florida
  'Miami, FL', 'Orlando, FL', 'Tampa, FL', 'Jacksonville, FL',
  // Maryland
  'Rockville, MD', 'Gaithersburg, MD', 'Bethesda, MD', 'Columbia, MD',
  // Other Major Metros
  'Portland, OR', 'Salt Lake City, UT', 'Las Vegas, NV', 'Kansas City, MO',
  'St. Louis, MO', 'Indianapolis, IN', 'Nashville, TN', 'Memphis, TN',
  'Louisville, KY', 'Hartford, CT', 'Providence, RI',
];

export const mockThreads: ForumThread[] = [
  {
    id: 'thread-001',
    title: 'H1B transfer timeline — how long did your new employer take to file?',
    preview: 'My current employer is being difficult about the transfer. New offer is great but I need to know realistic timelines. USCIS premium processing says 15 days but lawyer says expect 30-45 days total.',
    authorName: 'Rahul Mehta',
    authorInitials: 'RM',
    category: 'immigration',
    subcategory: 'h1b',
    city: 'San Jose, CA',
    upvotes: 87,
    replyCount: 34,
    createdAt: Date.now() - 1000 * 60 * 45,
    timeAgo: '45 min ago',
    pinned: true,
  },
  {
    id: 'thread-002',
    title: 'Referrals open at Cisco — SDE2 and Staff Eng positions (networking stack)',
    preview: 'I can refer folks for SDE2 and Staff Eng roles on the networking stack team in San Jose. Drop your LinkedIn + resume. Preference for folks with 4+ years networking experience.',
    authorName: 'Deepa Krishnamurthy',
    authorInitials: 'DK',
    category: 'jobs',
    subcategory: 'software-engineer',
    city: 'San Jose, CA',
    upvotes: 142,
    replyCount: 58,
    createdAt: Date.now() - 1000 * 60 * 90,
    timeAgo: '1.5 hrs ago',
    pinned: true,
  },
  {
    id: 'thread-003',
    title: 'Anyone renting in Fremont near BART? Looking for 2BHK under $3000',
    preview: 'Moving from Hyderabad in June. Wife and I need 2BHK near Fremont BART. Have seen listings on Zillow but want to connect with desi landlords who understand our situation — vegetarian, no pets.',
    authorName: 'Vikram Nair',
    authorInitials: 'VN',
    category: 'housing-forum',
    subcategory: 'apartment-rent',
    city: 'Fremont, CA',
    upvotes: 23,
    replyCount: 19,
    createdAt: Date.now() - 1000 * 60 * 180,
    timeAgo: '3 hrs ago',
    pinned: false,
  },
  {
    id: 'thread-004',
    title: 'SSN application after OPT approval — what documents did you carry?',
    preview: 'Got OPT approval last week. Going to SSA office next Monday. Confused about which I-20 to carry — original or the most recent one? Also do I need appointment or walk-in works?',
    authorName: 'Ananya Sharma',
    authorInitials: 'AS',
    category: 'immigration',
    subcategory: 'opt',
    city: 'Boston, MA',
    upvotes: 61,
    replyCount: 27,
    createdAt: Date.now() - 1000 * 60 * 300,
    timeAgo: '5 hrs ago',
    pinned: false,
  },
  {
    id: 'thread-005',
    title: 'Best Indian grocery stores in Seattle? Redmond specifically.',
    preview: 'New to Redmond. Missing home food badly. Found Patel Brothers but it\'s 45 min away. Any closer options? Specifically looking for fresh methi, curry leaves, and decent quality atta.',
    authorName: 'Suresh Iyer',
    authorInitials: 'SI',
    category: 'life',
    city: 'Redmond, WA',
    upvotes: 45,
    replyCount: 22,
    createdAt: Date.now() - 1000 * 60 * 480,
    timeAgo: '8 hrs ago',
    pinned: false,
  },
  {
    id: 'thread-006',
    title: 'Selling barely used Instant Pot Duo 8Qt — $45 in San Jose',
    preview: 'Moving back to India next month. Selling Instant Pot Duo 8Qt, bought 8 months ago, barely used. Comes with all accessories. Pickup only from Berryessa area. DM me.',
    authorName: 'Pooja Agarwal',
    authorInitials: 'PA',
    category: 'buysell',
    city: 'San Jose, CA',
    upvotes: 8,
    replyCount: 5,
    createdAt: Date.now() - 1000 * 60 * 720,
    timeAgo: '12 hrs ago',
    pinned: false,
  },
  {
    id: 'thread-007',
    title: 'Amazon SDE2 referral — L5 role in Alexa team, Austin TX',
    preview: 'Can refer for SDE2 (L5) in Alexa Shopping team in Austin. Must have 5+ years experience and strong Java/Python. System design rounds are heavy. DM your resume by this Friday.',
    authorName: 'Karthik Sundaram',
    authorInitials: 'KS',
    category: 'jobs',
    subcategory: 'software-engineer',
    city: 'Austin, TX',
    upvotes: 203,
    replyCount: 91,
    createdAt: Date.now() - 1000 * 60 * 60 * 16,
    timeAgo: '16 hrs ago',
    pinned: false,
  },
  {
    id: 'thread-008',
    title: 'Green Card I-485 pending 3 years — anyone else in same boat?',
    preview: 'EB2 India priority date current since Jan 2026 but my I-485 is still pending since April 2023. Infopass appointment shows "interview not required." Anyone filed mandamus?',
    authorName: 'Pradeep Venkatesh',
    authorInitials: 'PV',
    category: 'immigration',
    subcategory: 'green-card',
    city: 'Dallas, TX',
    upvotes: 174,
    replyCount: 68,
    createdAt: Date.now() - 1000 * 60 * 60 * 22,
    timeAgo: '22 hrs ago',
    pinned: false,
  },
  {
    id: 'thread-009',
    title: 'Driving license in California — which DMV office has shortest wait?',
    preview: 'Switching from Indian DL. I know the written test is mandatory. But which DMV in Bay Area has appointments available within 2 weeks? Tried online and everything is 6 weeks out.',
    authorName: 'Meena Balakrishnan',
    authorInitials: 'MB',
    category: 'life',
    city: 'Sunnyvale, CA',
    upvotes: 38,
    replyCount: 31,
    timeAgo: '1 day ago',
    createdAt: Date.now() - 1000 * 60 * 60 * 28,
    pinned: false,
  },
  {
    id: 'thread-010',
    title: 'Fully furnished room available in Raleigh — $950/mo, Indian household',
    preview: 'Looking for a working professional roommate in our 3BHK in North Raleigh. Indian vegetarian household. Room is fully furnished, utilities split. Female preferred. Available May 1st.',
    authorName: 'Gayatri Reddy',
    authorInitials: 'GR',
    category: 'housing-forum',
    subcategory: 'shared-room',
    city: 'Raleigh, NC',
    upvotes: 12,
    replyCount: 7,
    createdAt: Date.now() - 1000 * 60 * 60 * 36,
    timeAgo: '1.5 days ago',
    pinned: false,
  },
  {
    id: 'thread-011',
    title: 'Credit score tips for new H1B holders — best starter cards?',
    preview: 'Just got H1B stamped and moving to US in May. I know credit score is important. What secured cards should I apply for first? Heard Discover It and Capital One Platinum are good for beginners.',
    authorName: 'Rohan Joshi',
    authorInitials: 'RJ',
    category: 'life',
    city: 'Chicago, IL',
    upvotes: 96,
    replyCount: 44,
    createdAt: Date.now() - 1000 * 60 * 60 * 48,
    timeAgo: '2 days ago',
    pinned: false,
  },
  {
    id: 'thread-012',
    title: 'Microsoft referrals — SWE roles in Redmond and Atlanta (multiple levels)',
    preview: 'Can refer for multiple SWE positions at Microsoft — L59, L60, L62 in Redmond and Atlanta. Strong preference for .NET, Azure, and distributed systems experience. Deadline is April 30.',
    authorName: 'Aishwarya Patel',
    authorInitials: 'AP',
    category: 'jobs',
    subcategory: 'software-engineer',
    city: 'Redmond, WA',
    upvotes: 318,
    replyCount: 127,
    createdAt: Date.now() - 1000 * 60 * 60 * 55,
    timeAgo: '2.3 days ago',
    pinned: false,
  },
  {
    id: 'thread-013',
    title: 'Lease transfer available — 2BHK in Sunnyvale, $2800/mo, ends Dec 2026',
    preview: 'Relocating to NYC for a new job. Looking to transfer my lease for a 2BHK apartment in Sunnyvale. Rent is $2800/mo, lease ends December 2026. Landlord is flexible. DM for details.',
    authorName: 'Nikhil Sharma',
    authorInitials: 'NS',
    category: 'housing-forum',
    subcategory: 'transfer-lease',
    city: 'Sunnyvale, CA',
    upvotes: 19,
    replyCount: 11,
    createdAt: Date.now() - 1000 * 60 * 60 * 10,
    timeAgo: '10 hrs ago',
    pinned: false,
  },
  {
    id: 'thread-014',
    title: 'F1 to H1B cap-exempt — university job counts?',
    preview: 'I have a job offer from a state university. I know cap-exempt H1B is possible but my attorney is unsure about the timeline. Has anyone done F1 OPT → cap-exempt H1B at a university?',
    authorName: 'Priya Nambiar',
    authorInitials: 'PN',
    category: 'immigration',
    subcategory: 'f1',
    city: 'Boston, MA',
    upvotes: 44,
    replyCount: 21,
    createdAt: Date.now() - 1000 * 60 * 60 * 14,
    timeAgo: '14 hrs ago',
    pinned: false,
  },
  {
    id: 'thread-015',
    title: 'QA Engineer openings at Salesforce — manual + automation (SDET)',
    preview: 'Can refer for QA/SDET roles at Salesforce in San Francisco and Hyderabad. Must know Selenium, Java, and API testing. 3+ years experience required. Drop resume in DMs.',
    authorName: 'Sanjay Kulkarni',
    authorInitials: 'SK',
    category: 'jobs',
    subcategory: 'qa',
    city: 'San Jose, CA',
    upvotes: 76,
    replyCount: 33,
    createdAt: Date.now() - 1000 * 60 * 60 * 20,
    timeAgo: '20 hrs ago',
    pinned: false,
  },
];